<template>
  <div id="natcon-officers-award-create" class="professional-award-form">
    <!-- Overlay -->
    <v-overlay :value="OverlayLoadingFlag" class="professional-overlay">
      <div class="loading-container">
        <v-progress-circular
          indeterminate
          size="64"
          width="4"
          color="#1976d2"
        ></v-progress-circular>
        <p class="loading-text mt-4">Processing your award application...</p>
      </div>
    </v-overlay>

    <!-- Main -->
    <v-card class="main-form-container" elevation="4">
      <v-card-title class="professional-form-header pa-6">
        <div class="header-content">
          <div class="title-section">
            <div class="main-title-wrapper">
              <v-icon size="32" color="#1976d2" class="mr-3">mdi-trophy</v-icon>
              <div class="title-text">
                <h1 class="form-main-title">{{ PageTitle }}</h1>
                <div class="award-badge mt-2">
                  <v-chip color="#ff9800" text-color="white" small>
                    <v-icon left size="14">mdi-star</v-icon>
                    {{ AwardTypeTitleTxt }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>

          <div class="action-buttons">
            <router-link to="/national/officers-award/list">
              <v-btn
                color="#ff9800"
                elevation="2"
                rounded
                class="action-btn mr-3 white--text"
              >
                <v-icon left size="18">mdi-arrow-left</v-icon>
                Back
              </v-btn>
            </router-link>

            <v-btn
              @click.prevent="refreshPageData"
              color="#2ecc71"
              elevation="2"
              rounded
              class="action-btn white--text"
            >
              <v-icon left size="18">mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </div>
        </div>

        <div class="description-section mt-4" v-if="PageDescription">
          <div class="description-wrapper">
            <v-icon size="20" color="#666" class="mr-2"
              >mdi-information-outline</v-icon
            >
            <p class="form-description">{{ PageDescription }}</p>
          </div>
        </div>
      </v-card-title>

      <v-card-text class="form-main-content pa-6">
        <v-container>
          <div class="form-loading-section" v-if="LoadingFlag">
            <div class="loading-wrapper">
              <v-progress-circular
                indeterminate
                color="#1976d2"
                size="48"
                width="4"
              ></v-progress-circular>
              <p class="loading-text mt-4">Loading award criteria...</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12" v-if="!LoadingFlag">
              <!-- success state when member is registered for NatCon -->
              <template v-if="NatconRegistrationFlag">
                <v-card
                  class="natcon-alert-card pa-3 elevation-1 mt-3"
                  style="
                    background: rgba(0, 200, 83, 0.12);
                    border: 1px solid rgba(0, 200, 83, 0.3);
                  "
                >
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar size="56" class="mr-4" color="green lighten-5">
                        <v-icon color="green darken-2" size="28"
                          >mdi-check-circle</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div
                        class="natcon-alert-title"
                        style="font-weight: 600; color: #1b5e20"
                      >
                        National Conference Registration Verified
                      </div>

                      <div class="natcon-alert-msg" style="color: #2e7d32">
                        The member has successfully
                        <strong
                          >completed the National Conference
                          registration</strong
                        >.
                        <div
                          class="natcon-alert-extra mt-2"
                          style="color: #2e7d32"
                        >
                          You may continue with the award nomination process.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="green lighten-5"
                        text-color="green darken-2"
                      >
                        Verified
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- error state when not registered (keeps existing look + extra line) -->
              <template v-else>
                <v-card class="natcon-alert-card pa-3 elevation-1 mt-3">
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar
                        size="56"
                        class="mr-4 pulse-icon"
                        color="red lighten-5"
                      >
                        <v-icon color="red darken-2" size="28"
                          >mdi-account-alert</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div class="natcon-alert-title">
                        National Conference Registration Required
                      </div>
                      <div class="natcon-alert-msg">
                        The selected member has
                        <strong
                          >not completed the National Conference
                          registration</strong
                        >. Award nomination cannot proceed until registration is
                        done.
                        <div class="natcon-alert-extra mt-2">
                          Please ask the member to finish the NatCon
                          registration process, or contact support if assistance
                          is needed.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="red lighten-5"
                        text-color="red darken-2"
                      >
                        Action Required
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" v-if="!LoadingFlag">
              <!-- success state when member profile is verified -->
              <template v-if="ProfileStatusFlag">
                <v-card
                  class="natcon-alert-card pa-3 elevation-1 mt-3"
                  style="
                    background: rgba(0, 200, 83, 0.12);
                    border: 1px solid rgba(0, 200, 83, 0.3);
                  "
                >
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar size="56" class="mr-4" color="green lighten-5">
                        <v-icon color="green darken-2" size="28"
                          >mdi-check-circle</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div
                        class="natcon-alert-title"
                        style="font-weight: 600; color: #1b5e20"
                      >
                        Profile Verified ✓
                      </div>

                      <div class="natcon-alert-msg" style="color: #2e7d32">
                        This member's profile has been verified and is eligible
                        to apply for awards.
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="green lighten-5"
                        text-color="green darken-2"
                      >
                        Ready
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- error state when profile is not verified -->
              <template v-else>
                <v-card class="natcon-alert-card pa-3 elevation-1 mt-3">
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar
                        size="56"
                        class="mr-4 pulse-icon"
                        color="red lighten-5"
                      >
                        <v-icon color="red darken-2" size="28"
                          >mdi-alert-circle</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div class="natcon-alert-title">
                        ⚠ Profile Not Verified
                      </div>
                      <div class="natcon-alert-msg">
                        The selected member's profile has
                        <strong>not been verified</strong>. Profile verification
                        is required to proceed with award nomination.
                        <div class="natcon-alert-extra mt-2">
                          Please ask the member to complete their profile
                          verification, or contact support if assistance is
                          needed.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="red lighten-5"
                        text-color="red darken-2"
                      >
                        Blocked
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" v-if="!LoadingFlag">
              <!-- success state when member age is verified -->
              <template v-if="AgeVerificationFlag">
                <v-card
                  class="natcon-alert-card pa-3 elevation-1 mt-3"
                  style="
                    background: rgba(0, 200, 83, 0.12);
                    border: 1px solid rgba(0, 200, 83, 0.3);
                  "
                >
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar size="56" class="mr-4" color="green lighten-5">
                        <v-icon color="green darken-2" size="28"
                          >mdi-check-circle</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div
                        class="natcon-alert-title"
                        style="font-weight: 600; color: #1b5e20"
                      >
                        Eligible by age criteria ✓
                      </div>

                      <div class="natcon-alert-msg" style="color: #2e7d32">
                        This member's age is eligible to apply for this awards.
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="green lighten-5"
                        text-color="green darken-2"
                      >
                        Ready
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- error state when age is not verified -->
              <template v-else>
                <v-card class="natcon-alert-card pa-3 elevation-1 mt-3">
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar
                        size="56"
                        class="mr-4 pulse-icon"
                        color="red lighten-5"
                      >
                        <v-icon color="red darken-2" size="28"
                          >mdi-alert-circle</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div class="natcon-alert-title">
                        ⚠ You are not eligible due to Age criteria
                      </div>
                      <div class="natcon-alert-msg">
                        {{ AgeVerificationMsg }}
                        <div class="natcon-alert-extra mt-2">
                          Please contact support if assistance is needed.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="red lighten-5"
                        text-color="red darken-2"
                      >
                        Blocked
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>
            </v-col>
          </v-row>
          <br />

          <v-form
            ref="form1"
            v-model="valid1"
            lazy-validation
            v-on:submit.prevent="getTotalNatConOfficersAwardsDetails"
            class="professional-form"
          >
            <div class="form-content-wrapper">
              <v-card class="criteria-card mb-6" elevation="3">
                <v-card-title class="criteria-header">
                  <v-icon size="24" color="#1976d2" class="mr-3"
                    >mdi-clipboard-text</v-icon
                  >
                  <span class="section-title">Judging Criteria</span>
                </v-card-title>

                <v-card-text class="pa-6">
                  <v-row>
                    <v-col v-if="CriteriaAnswers.length == 0" cols="12">
                      <h5 class="text-center text-dark">Nil.</h5>
                    </v-col>

                    <v-col
                      v-else
                      cols="12"
                      v-for="item in CriteriaAnswers"
                      :key="item.SNo"
                    >
                      <template v-if="item.JudgingCriteriaDetailFlag">
                        <div class="criteria-detail-card mb-3">
                          <h3 class="criteria-title">
                            {{ item.JudgingCriteriaDetail }}
                            <small
                              >(score - {{ item.JudgingCriteriaScore }})</small
                            >
                          </h3>
                        </div>
                      </template>

                      <div class="question-content-wrapper">
                        <div class="question-label-section mb-2">
                          <h6 class="question-title">
                            <span class="required-indicator">*</span>
                            {{ item.CriteriaQuestionsText }}
                          </h6>
                        </div>

                        <div class="html-editor-wrapper">
                          <vue-editor
                            :id="item.AnswerId"
                            v-model="item.AnswerText"
                            :editor-toolbar="editorOptions.modules.toolbar"
                            :editorOptions="editorOptions"
                            @text-change="onTextChange($event, item)"
                            class="professional-editor"
                          ></vue-editor>
                          <div
                            class="character-counter"
                            :class="{
                              'error--text':
                                getCharacterCount(item.AnswerText) >
                                item.CriteriaQuestionsCharsCount,
                            }"
                          >
                            {{ getCharacterCount(item.AnswerText) }} /
                            {{ item.CriteriaQuestionsCharsCount }} characters
                          </div>
                        </div>
                      </div>

                      <div class="attachments-section mt-4">
                        <div
                          class="attachments-header d-flex justify-space-between align-center"
                        >
                          <h3 class="attachments-title">
                            <v-icon size="20" color="#ff9800" class="mr-2"
                              >mdi-paperclip</v-icon
                            >
                            Attachments
                            <small
                              >(Max:
                              {{ item.CriteriaQuestionsAttachmentMax }})</small
                            >
                          </h3>

                          <v-btn
                            color="primary"
                            fab
                            small
                            elevation="2"
                            class="add-attachment-btn"
                            v-if="
                              item.CriteriaQuestionsAttachmentMax >
                              item.AllProject.length
                            "
                            @click="addNewRow(item)"
                          >
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                        </div>

                        <div
                          v-if="item.AllProject.length > 0"
                          class="attachments-grid mt-4"
                        >
                          <v-row>
                            <v-col
                              cols="12"
                              sm="6"
                              md="4"
                              lg="3"
                              v-for="(data, index) in item.AllProject"
                              :key="index"
                              v-if="data.AnswerEventId != ''"
                            >
                              <v-card class="attachment-card" elevation="2">
                                <div class="attachment-actions">
                                  <v-btn
                                    icon
                                    color="error"
                                    small
                                    @click="deleteRow(item.SNo, index)"
                                    ><v-icon>mdi-delete</v-icon></v-btn
                                  >
                                </div>
                                <v-card-text>
                                  <div
                                    v-if="
                                      data.AnswerEventPhoto &&
                                      data.AnswerEventPhoto.length
                                    "
                                    class="event-images text-center mb-3"
                                  >
                                    <viewer
                                      rebuild
                                      :images="data.AnswerEventPhoto"
                                      ><img
                                        v-for="src in data.AnswerEventPhoto"
                                        :src="src"
                                        :key="src"
                                        class="event-image"
                                        width="100"
                                        height="100"
                                    /></viewer>
                                  </div>

                                  <div class="event-details">
                                    <div class="detail-row mb-2">
                                      <v-icon
                                        size="16"
                                        color="#666"
                                        class="mr-1"
                                        >mdi-calendar</v-icon
                                      ><span class="detail-label"
                                        >Event Date:</span
                                      ><span class="detail-value">{{
                                        data.AnswerEventDate
                                      }}</span>
                                    </div>
                                    <div class="detail-row mb-2">
                                      <v-icon
                                        size="16"
                                        color="#666"
                                        class="mr-1"
                                        >mdi-domain</v-icon
                                      ><span class="detail-label">Area:</span
                                      ><span class="detail-value">{{
                                        data.AnswerPortfolioName
                                      }}</span>
                                    </div>
                                    <div class="detail-row">
                                      <v-icon
                                        size="16"
                                        color="#666"
                                        class="mr-1"
                                        >mdi-tag</v-icon
                                      ><span class="detail-label"
                                        >Event Name:</span
                                      ><span class="detail-value">{{
                                        data.AnswerEventName
                                      }}</span>
                                    </div>
                                  </div>
                                </v-card-text>
                              </v-card>
                            </v-col>
                          </v-row>
                        </div>

                        <div class="no-attachments-state mt-4" v-else>
                          <v-alert
                            type="info"
                            text
                            color="grey lighten-1"
                            outlined
                            dense
                            >No attachments added yet. Click the + button to add
                            an attachment.</v-alert
                          >
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Additional Attachments Section -->
              <template v-if="CriteriaAnswers.length > 0">
                <v-card
                  class="attachments-additional-card mb-6 elevation-3"
                  style="margin-top: 2rem"
                >
                  <v-card-title class="attachments-additional-header">
                    <v-icon size="24" color="#ff9800" class="mr-3"
                      >mdi-image-multiple</v-icon
                    >
                    <span class="section-title">Additional Images</span>
                    <v-spacer></v-spacer>
                    <v-btn
                      v-if="AllProjectMax > AllProject.length"
                      icon
                      small
                      color="#1976d2"
                      class="add-attachment-action-btn"
                      @click="addNewRow()"
                      v-tooltip="'Add Supporting Image'"
                    >
                      <v-icon>mdi-plus-circle</v-icon>
                    </v-btn>
                  </v-card-title>

                  <v-card-text class="pa-6">
                    <!-- Info Alert -->
                    <v-alert type="info" text outlined class="info-alert mb-6">
                      <div class="d-flex align-center">
                        <v-icon size="20" color="#1976d2" class="mr-3"
                          >mdi-information-variant</v-icon
                        >
                        <div>
                          <strong>Image Requirements:</strong>
                          <div class="text-sm mt-1">
                            • Supported formats: JPG, PNG, JPEG<br />
                            • Maximum file size: 2MB per image<br />
                            • Maximum attachments: {{ AllProjectMax }}
                          </div>
                        </div>
                      </div>
                    </v-alert>

                    <!-- Empty State -->
                    <div
                      v-if="AllProject.length === 0"
                      class="empty-state-container"
                    >
                      <v-img
                        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 150'%3E%3Ccircle cx='100' cy='75' r='40' fill='%23e3f2fd'/%3E%3Cpath d='M85 65h30v20H85z' fill='%231976d2'/%3E%3Cpath d='M95 75h10v20H95z' fill='%231976d2'/%3E%3C/svg%3E"
                        max-width="100"
                        class="mx-auto"
                      ></v-img>
                      <p class="text-center text-grey mt-4 mb-0">
                        No supporting images added yet.<br />
                        <small
                          >Click the + button to add supporting images</small
                        >
                      </p>
                    </div>

                    <!-- Attachments Grid -->
                    <v-row v-else class="image-gallery">
                      <v-col
                        v-for="(data, index) in AllProject"
                        cols="12"
                        sm="6"
                        md="4"
                        lg="3"
                        :key="index"
                      >
                        <v-card class="image-card elevation-2" hover>
                          <div class="image-delete-action">
                            <v-btn
                              icon
                              x-small
                              color="white"
                              class="delete-btn"
                              @click="deleteRow(index)"
                              v-tooltip="'Delete Image'"
                            >
                              <v-icon>mdi-close-circle</v-icon>
                            </v-btn>
                          </div>
                          <v-img
                            :src="data.AnswerEventImageUrl"
                            aspect-ratio="1"
                            class="image-preview"
                          ></v-img>
                          <v-card-text class="image-details pa-3">
                            <div class="detail-item mb-2">
                              <v-icon size="14" color="#ff9800" class="mr-1"
                                >mdi-calendar</v-icon
                              >
                              <small class="detail-label">Date:</small>
                              <small class="detail-value">{{
                                data.AnswerEventDate
                              }}</small>
                            </div>
                            <div class="detail-item">
                              <v-icon size="14" color="#1976d2" class="mr-1"
                                >mdi-tag</v-icon
                              >
                              <small class="detail-label">Event:</small>
                              <small
                                class="detail-value text-truncate"
                                :title="data.AnswerEventName"
                              >
                                {{ data.AnswerEventName }}
                              </small>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </template>

              <!-- Video Upload Section -->
              <v-card
                v-if="CriteriaAnswers.length > 0"
                class="video-upload-card elevation-3"
                style="margin-top: 1rem"
              >
                <v-card-title class="video-upload-header">
                  <v-icon size="24" color="#d32f2f" class="mr-3"
                    >mdi-video</v-icon
                  >
                  <span class="section-title">Video Submission</span>
                </v-card-title>

                <v-card-text class="pa-6">
                  <!-- Video Info Alert -->
                  <v-alert
                    type="warning"
                    text
                    outlined
                    class="video-info-alert mb-6"
                  >
                    <div class="d-flex align-center">
                      <v-icon size="20" color="#d32f2f" class="mr-3"
                        >mdi-alert-circle-outline</v-icon
                      >
                      <div>
                        <strong>Video Requirements:</strong>
                        <div class="text-sm mt-1">
                          • Supported formats: MP4, AVI, MOV<br />
                          • Maximum file size: 25MB<br />
                          • This field is <strong>optional</strong>
                        </div>
                      </div>
                    </div>
                  </v-alert>

                  <!-- Video Upload Input -->
                  <v-row>
                    <v-col cols="12" md="8">
                      <div class="upload-wrapper">
                        <label class="upload-label">
                          <v-icon size="20" color="#d32f2f" class="mr-2"
                            >mdi-video-plus</v-icon
                          >
                          Select Video File
                        </label>
                        <v-file-input
                          ref="UploadVideo"
                          v-model="UploadVideo"
                          :rules="UploadVideoRules"
                          counter
                          show-size
                          truncate-length="50"
                          :clearable="false"
                          prepend-icon="mdi-folder-open"
                          accept="video/*"
                          outlined
                          dense
                          class="video-input"
                        >
                          <template v-slot:append-outer>
                            <v-btn
                              icon
                              x-small
                              color="error"
                              @click="resetUploadVideo"
                              v-if="UploadVideo.length > 0"
                              v-tooltip="'Clear Video'"
                            >
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                        </v-file-input>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center">
                      <div
                        class="video-preview-info"
                        v-if="UploadVideo.length > 0"
                      >
                        <v-chip
                          color="#d32f2f"
                          text-color="white"
                          small
                          class="video-chip"
                        >
                          <v-icon left size="16">mdi-check-circle</v-icon>
                          Video Selected
                        </v-chip>
                      </div>
                      <div class="video-preview-info" v-else>
                        <p class="text-grey text-center mb-0">
                          <small>No video uploaded</small>
                        </p>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <v-row wrap v-if="ProgessStart == 1">
                <v-col align="center" cols="12" md="12">
                  <v-progress-circular
                    :rotate="180"
                    :size="100"
                    :width="15"
                    :value="uploadPercentage"
                    color="#00a4ef"
                  >
                    {{ uploadPercentage }}
                  </v-progress-circular>
                  <h3 class="red--text">
                    Please wait, we are uploading your details. Time may vary
                    based on your internet connection.
                  </h3>
                </v-col>
              </v-row>
              <br />
              <!-- Professional Submit Section -->
              <div class="submit-section" v-if="CriteriaAnswers.length > 0">
                <v-card class="submit-card" elevation="3">
                  <v-card-text class="text-center pa-6">
                    <div class="submit-info mb-4">
                      <h3 class="submit-title">Ready to Submit?</h3>
                      <p class="submit-description">
                        You can save your progress or submit your final
                        application.
                      </p>
                    </div>

                    <div class="action-buttons">
                      <v-btn
                        @click.prevent="getTotalNatConOfficersAwardsDetails(4)"
                        :loading="SaveFlag"
                        :disabled="
                          !valid1 ||
                          !NatconRegistrationFlag ||
                          !ProfileStatusFlag ||
                          !AgeVerificationFlag
                        "
                        elevation="3"
                        rounded
                        large
                        color="#4caf50"
                        class="submit-btn white--text mr-4"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Save Draft
                      </v-btn>

                      <v-btn
                        :disabled="
                          !valid1 ||
                          !NatconRegistrationFlag ||
                          !ProfileStatusFlag ||
                          !AgeVerificationFlag
                        "
                        @click.prevent="getTotalNatConOfficersAwardsDetails(3)"
                        :loading="SubmitFlag"
                        elevation="3"
                        rounded
                        large
                        color="#1976d2"
                        class="submit-btn white--text"
                      >
                        <v-icon left>mdi-send</v-icon>
                        Final Submit
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </v-form>
        </v-container>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="addRecordPrompt"
      persistent
      max-width="800px"
      transition="dialog-transition"
      overlay-opacity="0.7"
    >
      <attachment-create
        pageTitle="Add Project Details"
        :recordData="selectedRecord"
        :addRecordPrompt="addRecordPrompt"
        @hideAddRecordPrompt="hideAddRecordPrompt($event)"
        v-if="addRecordPrompt"
      ></attachment-create>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

import AttachmentCreate from "@/view/pages/erp/jci-award/natcon/common/AttachmentCreate.vue";
import { VueEditor } from "vue2-editor"; // Import Vue-Quill-Editor

export default {
  mixins: [common],
  components: {
    AttachmentCreate,
    VueEditor,
  },
  data() {
    return {
      ProgessStart: 0,
      uploadPercentage: 0,

      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SubmitFlag: false,
      SaveFlag: false,
      SearchFlag: false,
      CardLoadingFlag: false,
      UploadFlag: false,
      addButtonFlag: true,
      OverlayLoadingFlag: false,
      NatconRegistrationFlag: false,
      ProfileStatusFlag: false,
      MissingSteps: "",
      Status: "",
      AgeVerificationFlag: false,
      AgeVerificationMsg: "",

      NatconOfficersAwardStartDate: "",
      NatconOfficersAwardEndDate: "",
      TotalNatconOfficersAward: 0,
      TotalApplied: 0,
      TotalPartiallySaved: 0,
      NatconRegistrationCount: "",

      editorOptions: {
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
          ],
        },
        placeholder: "Please provide your detailed answer here...",
      },

      StartDateFlag: false,
      EndDateFlag: false,

      UploadVideo: [],
      UploadVideoRules: [
        (value) =>
          !value ||
          value.size < 25000000 ||
          "Upload Video size should be less than 25 MB!",
      ],

      rows: {},
      AwardTypeTitleTxt: "",
      AwardTypeId: "",
      CriteriaAnswers: [],
      Project: [],
      AllProjectMax: 10,
      AllProject: [],
      AllImages: [],
      selectedRecord: "",
      Id: "",
      showTextBox: false,

      addRecordPrompt: false,

      ImageUploadFlag: false,

      AnswerRules: [(v) => !!v || "Answer is required"],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var zone_name = this.$session.get("ZoneName");
        zone_name = zone_name == (null || undefined) ? 0 : zone_name;

        this.PageTitle = tr.PageTitle + " - " + zone_name;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        this.pageData();
      }
    },
  },
  mounted() {},
  methods: {
    // HTML Editor related methods
    getCharacterCount(html) {
      if (!html) return 0;
      // Remove HTML tags and entities to count only visible text
      const text = html.replace(
        /<[^>]*>|&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;/g,
        ""
      );
      return text.length;
    },

    onTextChange(event, item) {
      // Get the Quill editor instance from the event
      const editor = event.editor;
      const maxLength = item.CriteriaQuestionsCharsCount;

      // Calculate the current text length without HTML tags
      const currentLength = this.getCharacterCount(item.AnswerText);

      // If the length exceeds the limit, prevent further input
      if (currentLength > maxLength) {
        // Get the current selection
        const range = editor.getSelection();

        // If there is no selection, we need to restore the previous text
        if (range && range.length === 0) {
          // Delete the last character
          const position = range.index - 1;
          if (position >= 0) {
            editor.deleteText(position, 1);
          }
        }
      }

      // Update validation rules
      if (currentLength >= maxLength) {
        if (!this.AnswerRules.includes(this.validateMaxLength)) {
          this.AnswerRules.push(this.validateMaxLength);
        }
      } else {
        const index = this.AnswerRules.indexOf(this.validateMaxLength);
        if (index !== -1) {
          this.AnswerRules.splice(index, 1);
        }
      }
    },

    validateMaxLength(v) {
      // This is a custom validation rule for the rich text editor
      // It will be used by Vuetify's validation system
      const item = this.CriteriaAnswers.find((item) => item.AnswerText === v);
      if (
        item &&
        this.getCharacterCount(v) > item.CriteriaQuestionsCharsCount
      ) {
        return "Character limit exceeded";
      }
      return true;
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "national_officers_award",
        Action: "create",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");

      var id = this.$route.query.id;
      console.log({ id });
      this.AwardTypeId = id;

      this.getAwardTypeDetials();
      this.getCriteriaAnswers();
    },
    resetUploadVideo() {
      console.log("resetUploadVideo called.");
      this.UploadVideo = "";
    },
    resetForm() {
      console.log("resetForm called");
      var rows = this.CriteriaAnswers;

      rows.map((row, index) => {
        row.AnswerText="";
      });

      this.AllProject = {};
      this.AllImages = {};
      this.getAwardTypeDetials();
      this.getCriteriaAnswers();
    },
    addNewRow() {
      console.log("addNewRow called");
      this.addRecordPrompt = true;
    },
    deleteRow(id) {
      console.log("deleteRow called");
      console.log({ id });

      this.AllProject.splice(id, 1);
      this.AllImages.splice(id, 1);
    },
    hideAddRecordPrompt(tr) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      console.log({ tr });

      if (tr.EventName != "") {
        this.AllProject.push({
          AnswerEventId: "",
          AnswerEventName: tr.EventName,
          AnswerEventDate: tr.EventDate,
          AnswerEventDescription: tr.EventDescription,
          AnswerEventImageName: tr.EventImageName,
          AnswerEventImageUrl: tr.EventImageUrl,
          AnswerEventImage: tr.EventImage,
        });

        this.AllImages.push({
          AnswerEventId: "",
          AnswerEventName: tr.EventName,
          UploadedImage: tr.EventImage.UploadedImage,
        });
      }
      console.log("AllProject = " + JSON.stringify(this.AllProject));
    },
    getAwardTypeDetials() {
      console.log("getAwardTypeDetials called");

      var validate1 = true;
      console.log({ validate1 });

      if (validate1) {
        this.CardLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award-type/show";

        var upload = {
          UserInterface: 1,
          AwardType: this.AwardTypeId,
          AwardGroup: 3,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;
        this.NatconRegistrationFlag = false;
        this.NatconRegistrationCount = "";
        this.ProfileStatusFlag = false;
        var ProfileStatusFlag = false;
        this.MissingSteps = "";
        this.AgeVerificationFlag = false;
        this.AgeVerificationMsg = "";
        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.CardLoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              console.log("records = " + JSON.stringify(records));
              thisIns.AwardTypeTitleTxt = records.AwardTypeTitleTxt;
              thisIns.NatconRegistrationFlag = records.NatconRegistrationFlag;
              thisIns.NatconRegistrationCount = records.NatconRegistrationCount;

              var ProfileStatusFlag = records.ProfileStatusFlag;
              thisIns.ProfileStatusFlag = records.ProfileStatusFlag;
              thisIns.MissingSteps = records.MissingSteps;
              console.log({
                ProfileStatusFlag,
              });

              thisIns.AgeVerificationFlag = records.AgeVerificationFlag;
              thisIns.AgeVerificationMsg = records.AgeVerificationMsg;
              // Check if profile is updated/verified
              if (!ProfileStatusFlag) {
                Swal.fire({
                  icon: "error",
                  title: "Profile Not Verified",
                  html: `<div style="text-align: left;">
          <p><strong>Member Profile Verification Required</strong></p>
          <p>The selected member's profile is not yet verified.</p>
          <p style="margin-bottom: 0; color: #d32f2f;">
            <i class="mdi mdi-alert-circle" style="font-size:20px; vertical-align:middle;"></i>
            Please ensure the member completes their profile verification before applying for this award.
          </p>
        </div>`,
                  confirmButtonColor: "#d32f2f",
                  confirmButtonText: "OK",
                });
              }
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            thisIns.CardLoadingFlag = false;
            console.log("error=" + error);
            thisIns.toast("error", error);
          });
      } else {
        var message = "";
        console.log("error=" + message);
      }
    },
    getCriteriaAnswers() {
      console.log("getCriteriaAnswers called");

      var AwardType = this.AwardTypeId;
      console.log({ AwardType });

      if (AwardType > 0) {
        this.tableData1 = [];
        this.selected1 = [];
        this.LoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judging-criteria/questions-lists";
        var upload = {
          UserInterface: 1,
          AwardType: AwardType,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = [];
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.CriteriaAnswers = records;
              // thisIns.toast("success", output);
            } else {
              console.log("error=" + output);
              // thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
          });
      } else {
        var message = "Kindy select the Award Type";
        this.toast("error", message);
      }
    },
    getTotalNatConOfficersAwardsDetails(status) {
      console.log("getTotalNatConOfficersAwardsDetails called");

      var validate1 = true;
      console.log({ validate1 });

      console.log({ status });
      this.Status = status;

      if (validate1) {
        this.CardLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award-type/total-awards";

        var AwardCategory = 6;
        var AwardGroup = 3;

        if (this.RoleId == 2) {
          // For Admin
          var upload = {
            UserInterface: 1,
            AwardCategory: AwardCategory,
            AwardGroup: AwardGroup,
            Lom: this.LomCode,
          };
        } else {
          // For Others
          var upload = {
            UserInterface: 1,
            AwardCategory: AwardCategory,
            AwardGroup: AwardGroup,
          };
        }

        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.CardLoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              console.log("records = " + JSON.stringify(records));
              thisIns.rows = records;
              thisIns.NatconOfficersAwardStartDate = records.StartDateTxt;
              thisIns.NatconOfficersAwardEndDate = records.EndDateTxt;
              thisIns.TotalNatconOfficersAward = records.TotalAward;
              thisIns.TotalApplied = records.TotalApplied;
              thisIns.TotalPartiallySaved = records.TotalPartiallySaved;
              thisIns.StartDateFlag = records.StartDateFlag;
              thisIns.EndDateFlag = records.EndDateFlag;

              console.log(
                "StartDateFlag=" +
                  records.StartDateFlag +
                  ", EndDateFlag=" +
                  records.EndDateFlag
              );

              if (thisIns.StartDateFlag && thisIns.EndDateFlag) {
                thisIns.confirmAlert(status);
              } else {
                thisIns.deadlineAlert();
              }
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            thisIns.CardLoadingFlag = false;
            console.log("error=" + error);
            thisIns.toast("error", error);
          });
      } else {
        var message = "";
        console.log("error=" + message);
      }
    },
    deadlineAlert() {
      console.log("deadlineAlert called");
      var htmlTxt = "";
      htmlTxt +=
        "<h3> Natcon Officers Awards Bidding ended at <b> " +
        this.NatconOfficersAwardEndDate +
        " </b> </h3>";
      // htmlTxt += "<br/> Start <b> " + this.NatconOfficersAwardEndDate + " </b>";

      Swal.fire({
        title: "Sorry !",
        text: "",
        icon: "warning",
        html: htmlTxt,
      });
    },
    confirmAlert(status) {
      console.log("confirmAlert called");
      var video_validate = false;
      var validate1 = false;

      video_validate =
        this.UploadVideo == "" ? true : this.$refs.UploadVideo.validate();
      validate1 = status == 4 ? true : this.$refs.form1.validate();
      console.log({ status }, { video_validate }, { validate1 });

      if (validate1 && video_validate) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          if (result.isConfirmed) {
            // Delete Empty event id in AllProject
            var rows = this.CriteriaAnswers;

            rows.map((row, index) => {
              console.log({ index });
              var n2 = row.AllProject.length;
              console.log({ n2 });
              if (n2 == 1) {
                // iterate foreach for AllProject

                row.AllProject.map((data, k) => {
                  console.log({ k });
                  var eventname = data.AnswerEventName;
                  console.log({ eventname });
                  if (eventname == "") {
                    row.AllProject.splice(k, 1);
                  }
                });
              }
            });

            console.log("CriteriaAnswers = ", this.CriteriaAnswers);
            this.CriteriaAnswers = this.CriteriaAnswers;

            this.submitForm(status);
          } else {
            this.sweetAlert("error", "You cancelled the process", false);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "kindly answer all the questions.";
        }
        if (!video_validate) {
          message +=
            "The video should be <br/> 1. In MP4 or AVI or MOV file format <br/> 2. Must be less than 25MB in file size";
        }

        Swal.fire({
          // title: "Are you sure?",
          icon: "error",
          html: message,
        });
        // this.sweetAlert("error", message, false);
      }
    },
    submitForm(status) {
      console.log("submitForm is called");
      this.ProgessStart = 1;
      this.OverlayLoadingFlag = true;

      if (status == 4) {
        this.SaveFlag = true;
      } else {
        this.SubmitFlag = true;
      }

      var AwardGroup = 3;
      console.log({ status });
      var count = this.AllImages.length;

      var video = this.UploadVideo != {} ? this.UploadVideo : {};
      console.log({ video });

      this.AllImages.forEach((row, index) => {
        console.log({ index });
        row.AnswerEventId = index;
        console.log("AllImages=" + JSON.stringify(this.AllImages));
      });

      this.AllProject.forEach((item, index) => {
        console.log({ index });
        item.AnswerEventId = index;
        console.log("AllProject=" + JSON.stringify(this.AllProject));
      });

      var answerData = {
        ActiveStatus: status,
        // AwardGroup: AwardGroup,
        AwardType: this.AwardTypeId,
        CriteriaAnswers: this.CriteriaAnswers,
        AllProject: this.AllProject,
        AllImagesCount: count,
      };
      console.log("answerData=" + JSON.stringify(answerData));

      let upload = new FormData();
      upload.append("UserInterface", 1);
      upload.append("Form", JSON.stringify(answerData));
      upload.append("UploadVideo", video);

      this.AllImages.forEach((row, index) => {
        var id = index + 1;
        console.log({ id });
        var UploadedImage = row["UploadedImage"];
        var EventId = row["AnswerEventId"];
        console.log({ UploadedImage }, { EventId });

        upload.append("AllImages" + index, UploadedImage, EventId);
      });

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      var add_url = server_url + "api/award/officers-award-store";
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          // "Content-Type": "application/json",
          "Content-Type": "multipart/form-data",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));
          thisIns.ProgessStart = 0;
          thisIns.OverlayLoadingFlag = false;
          if (status == 4) {
            thisIns.SaveFlag = false;
          } else {
            thisIns.SubmitFlag = false;
          }

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);
          records = response.data.records;
          console.log({ records });

          if (flag == 1) {
            thisIns.sweetAlert("success", output, true);
            thisIns.$router.push("/national/officers-award/list");
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.ProgessStart = 0;
          thisIns.OverlayLoadingFlag = false;
          if (status == 4) {
            thisIns.SaveFlag = false;
          } else {
            thisIns.SubmitFlag = false;
          }
        });
    },
  },

  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
#natcon-officers-award-create {
  background-color: #f5f7fa;
  min-height: 100vh;

  .professional-overlay {
    .loading-container {
      text-align: center;
      color: white;
      .loading-text {
        font-size: 1.1rem;
        font-weight: 500;
        margin: 0;
      }
    }
  }

  .main-form-container {
    border-radius: 12px !important;
    overflow: hidden;
    background: white;
    box-shadow: 0 6px 30px rgba(0, 0, 0, 0.1) !important;
  }

  .professional-form-header {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-bottom: 1px solid #e3f2fd;
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      width: 100%;
    }
    .action-buttons {
      display: flex;
      gap: 0.5rem;
    }
    .form-main-title {
      font-size: 1.75rem;
      font-weight: 700;
      color: #2c3e50;
      margin: 0;
    }
    .award-badge {
      margin-top: 0.5rem;
    }
  }

  .description-section {
    .description-wrapper {
      display: flex;
      align-items: center;
      background: rgba(25, 118, 210, 0.05);
      padding: 1rem;
      border-radius: 8px;
      border-left: 4px solid #1976d2;
      .form-description {
        color: #424242;
        margin: 0;
        font-weight: 400;
      }
    }
  }

  .form-main-content {
    background: #f5f7fa;
  }

  .form-loading-section {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    .loading-wrapper {
      text-align: center;
      .loading-text {
        font-size: 1.1rem;
        color: #666;
        font-weight: 500;
        margin: 0;
      }
    }
  }

  .criteria-card,
  .submit-card {
    border-radius: 12px !important;
    border: 1px solid #e0e0e0;
  }

  .criteria-header {
    background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
    border-bottom: 1px solid #e0e0e0;
    padding: 1.5rem;
    .section-title {
      font-size: 1.3rem;
      font-weight: 600;
      color: #2c3e50;
    }
  }

  .criteria-detail-card {
    display: flex;
    align-items: center;
    background: #fff3e0;
    border: 1px solid #ffb74d;
    border-radius: 8px;
    padding: 12px;
    .criteria-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #e65100;
      margin: 0;
    }
  }

  .question-content-wrapper {
    margin-bottom: 1.5rem;
  }

  .question-label-section {
    .question-title {
      font-size: 1rem;
      font-weight: 600;
      color: #263238;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
  }

  .html-editor-wrapper {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    overflow: hidden;
  }

  .professional-editor .ql-container {
    min-height: 140px;
  }

  .character-counter {
    padding: 6px 10px;
    font-size: 0.85rem;
    color: #607d8b;
    text-align: right;
    background: #fafafa;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  .character-counter.error--text {
    color: #d32f2f;
    background: rgba(244, 67, 54, 0.06);
    font-weight: 600;
  }

  .professional-textarea .v-input__slot {
    min-height: 120px;
  }

  .attachments-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .attachments-grid .attachment-card {
    border-radius: 8px !important;
    position: relative;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .attachment-actions {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 2;
  }

  .event-image {
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    object-fit: cover;
    transition: transform 0.2s;
  }
  .event-image:hover {
    transform: scale(1.05);
  }

  .natcon-alert-card {
    border-left: 4px solid #ff0505;
    background: linear-gradient(
      90deg,
      rgba(25, 118, 210, 0.03),
      rgba(255, 255, 255, 0.01)
    );
    border-radius: 8px;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }
  .natcon-alert-title {
    font-weight: 700;
    font-size: 1.05rem;
    color: #ff0505;
  }
  .natcon-alert-msg {
    color: #ff0505;
    margin-top: 6px;
    font-size: 0.95rem;
    line-height: 1.35;
  }
  .natcon-alert-extra {
    color: rgba(198, 40, 40, 0.95);
    font-size: 0.92rem;
    line-height: 1.3;
  }

  .natcon-success-card {
    border-left: 4px solid #2e7d32;
    background: linear-gradient(
      90deg,
      rgba(46, 125, 50, 0.04),
      rgba(255, 255, 255, 0.01)
    );
    border-radius: 8px;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }
  .natcon-success-card:hover {
    box-shadow: 0 6px 18px rgba(46, 125, 50, 0.08);
    transform: translateY(-2px);
  }

  .pulse-icon {
    animation: pulse 1.4s infinite ease-in-out;
  }
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.12);
      opacity: 0.85;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  .submit-btn {
    text-transform: none;
    font-weight: 600;
  }

  .missing-steps-tooltip {
    padding: 8px 0;

    .tooltip-title {
      font-weight: 700;
      font-size: 0.8rem;
      color: #fff;
      margin-bottom: 6px;
      text-transform: uppercase;
    }

    .missing-steps-list {
      font-size: 0.75rem;
      color: #fff;
      line-height: 1.4;
      white-space: normal;
      word-wrap: break-word;
    }
  }

  /* Additional Attachments Section */
  .attachments-additional-card {
    border-radius: 12px !important;
    border: 1px solid #e0e0e0;
    background: white;
  }

  .attachments-additional-header {
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    border-bottom: 1px solid #ffb74d;
    padding: 1.5rem;

    .section-title {
      font-size: 1.3rem;
      font-weight: 600;
      color: #e65100;
    }
  }

  .add-attachment-action-btn {
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.15);
      background: rgba(25, 118, 210, 0.1) !important;
    }
  }

  .info-alert {
    background: rgba(25, 118, 210, 0.04) !important;
    border: 1px solid rgba(25, 118, 210, 0.2) !important;
    border-radius: 8px;

    .text-sm {
      font-size: 0.9rem;
      line-height: 1.4;
      color: #424242;
    }
  }

  .empty-state-container {
    text-align: center;
    padding: 2rem 1rem;
    border: 2px dashed #ccc;
    border-radius: 8px;
    background: #f5f5f5;
  }

  .image-gallery {
    .image-card {
      border-radius: 8px !important;
      position: relative;
      overflow: hidden;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
      }

      .image-delete-action {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 10;
        background: rgba(211, 47, 47, 0.9);
        border-radius: 0 0 0 8px;
        padding: 2px;

        .delete-btn {
          transition: all 0.2s ease;

          &:hover {
            background: rgba(211, 47, 47, 1) !important;
          }
        }
      }

      .image-preview {
        border-bottom: 1px solid #e0e0e0;
      }

      .image-details {
        background: #fafafa;

        .detail-item {
          display: flex;
          align-items: center;
          gap: 4px;

          .detail-label {
            font-weight: 600;
            color: #424242;
            min-width: 40px;
          }

          .detail-value {
            color: #666;
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }
  }

  /* Video Upload Section */
  .video-upload-card {
    border-radius: 12px !important;
    border: 1px solid #e0e0e0;
    background: white;
  }

  .video-upload-header {
    background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
    border-bottom: 1px solid #ef5350;
    padding: 1.5rem;

    .section-title {
      font-size: 1.3rem;
      font-weight: 600;
      color: #c62828;
    }
  }

  .video-info-alert {
    background: rgba(211, 47, 47, 0.04) !important;
    border: 1px solid rgba(211, 47, 47, 0.2) !important;
    border-radius: 8px;

    .text-sm {
      font-size: 0.9rem;
      line-height: 1.4;
      color: #424242;
    }
  }

  .upload-wrapper {
    .upload-label {
      display: flex;
      align-items: center;
      font-weight: 600;
      color: #424242;
      margin-bottom: 12px;
      font-size: 0.95rem;
    }

    .video-input {
      &:deep(.v-input__slot) {
        min-height: 56px;
        background: #f5f5f5;
        border-radius: 8px;

        input {
          color: #424242;
        }
      }
    }
  }

  .video-preview-info {
    .video-chip {
      display: flex;
      gap: 6px;
      font-weight: 500;
    }
  }

  @media (max-width: 1024px) {
    .professional-form-header .header-content {
      flex-direction: column;
      gap: 1rem;
      .action-buttons {
        align-self: flex-start;
      }
    }
  }
  @media (max-width: 768px) {
    .main-form-container {
      margin: 0.5rem;
      border-radius: 8px !important;
    }
    .professional-form-header {
      padding: 1rem !important;
      .form-main-title {
        font-size: 1.5rem;
      }
    }
    .action-buttons {
      flex-direction: column;
      .submit-btn {
        width: 100%;
        margin: 0.25rem 0 !important;
      }
    }
  }

  /* dialog */
  .v-dialog .v-card {
    border-radius: 12px;
    overflow: hidden;
  }
}
</style>